import { authApi } from "@/api/auth-api";
import { authService } from "@/services/auth-service";
import { useWorkspaceStore } from "@/store/workspace-store";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import posthog from "posthog-js";

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isInitialized: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      isInitialized: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const data = await authApi.login(email, password);

          if (!data.user || !data.tokens) {
            throw new Error("Invalid response from server");
          }

          // Store tokens in auth service
          authService.setTokens(data.tokens);

          // Set user from response
          set({
            user: data.user,
            isLoading: false,
            isInitialized: true,
          });
        } catch (error) {
          set({
            isLoading: false,
            user: null,
          });
          authService.clearTokens();
          throw error;
        }
      },

      logout: async () => {
        const refreshToken = authService.getRefreshToken();

        try {
          // Call logout endpoint with refresh token
          await authApi.logout(refreshToken);
        } catch (error) {
          console.error("Logout API call failed:", error);
        } finally {
          // Clear tokens regardless of API call success
          authService.clearTokens();

          // Reset PostHog to clear identified user
          posthog.reset();

          // Clear both auth and workspace stores
          set({ user: null, isInitialized: false });
          useWorkspaceStore.setState({
            workspaces: [],
            activeWorkspaceId: null,
            activeWorkspace: null,
            isInitialized: false,
          });
        }
      },

      checkAuth: async () => {
        set({ isLoading: true });
        try {
          // Check if we have a valid session
          const hasSession = await authService.hasValidSession();

          if (hasSession) {
            // Get user from token
            const user = authService.getUserFromToken();
            if (user) {
              set({
                user,
                isLoading: false,
                isInitialized: true,
              });
              return;
            }
          }

          set({
            user: null,
            isLoading: false,
            isInitialized: true,
          });
        } catch (error) {
          console.error("Auth check failed:", error);
          set({
            user: null,
            isLoading: false,
            isInitialized: true,
          });
        }
      },

      refreshToken: async () => {
        try {
          await authService.refreshAccessToken();
          // Update user from new token
          const user = authService.getUserFromToken();
          if (user) {
            set({ user });
          }
        } catch (error) {
          console.error("Token refresh failed:", error);
          // If refresh fails, clear auth state
          get().logout();
          throw error;
        }
      },
    }),
    {
      name: "auth-storage",
      partialize: () => ({
        // Don't persist user anymore since it comes from JWT
        // We'll restore from token on app init
      }),
    }
  )
);
